/**
 * Baseline collector — accumulate labeled perf snapshots across scenarios.
 *
 * Capturing a baseline by hand is fiddly: the numbers live on the HUD and in the
 * spike recorder, they reset on reload, and there's no way to label "idle table"
 * vs "active hand" or paste them somewhere. This module fixes that. While the HUD
 * is open it exposes four console hooks (see PerfHud):
 *
 *   captureBaseline('idle table')   // snapshot the current run under a label
 *   dumpBaseline()                  // console.table of every capture so far
 *   copy(getBaseline())             // copy the full JSON to paste into the doc
 *   clearBaseline()                 // start over
 *
 * Captures persist in localStorage, so you can capture a scenario, reload for a
 * clean slate, capture the next, and still dump them all together at the end.
 *
 * A snapshot combines the live HUD stats (frame/mem/tween/rtt), which PerfHud
 * pushes here every sample via updateHudSnapshot, with the spike recorder's
 * session totals + top offenders.
 */
import { getSpikeSummary } from './spikeRecorder';

const STORAGE_KEY = 'hijack_perf_baseline';
const MAX_ENTRIES = 50; // guard localStorage against an unbounded session

// The subset of PerfHud's Stats we snapshot. Stats is a structural superset, so
// PerfHud can pass its whole stats object straight in.
export type HudSnapshot = {
  fps: number;
  fpsMin: number;
  worstFrameMaxMs: number;
  heapUsedMb: number;
  heapPeakMb: number;
  heapLeakMbPerMin: number;
  heapLeakReady: boolean;
  activeTweensMax: number;
  msgsPerSec: number;
  wsRttMs: number;
  wsRttMaxMs: number;
};

/** One saved capture: a labeled row of the numbers that matter for the baseline. */
export type BaselineEntry = {
  label: string;
  at: string; // ISO timestamp
  fps: number;
  fpsMin: number;
  worstFrameMaxMs: number;
  spikes: number;
  spikeWorstMs: number;
  topOffenders: string[]; // up to 3× "func @ source (Nms, N hits)"
  heapUsedMb: number;
  heapPeakMb: number;
  leakMbPerMin: number | null; // null until the leak trend is trustworthy
  tweensMax: number;
  rttMs: number;
  rttMaxMs: number;
};

let latest: HudSnapshot | null = null;

/** PerfHud calls this each sample so captureBaseline can read the live numbers. */
export const updateHudSnapshot = (s: HudSnapshot): void => {
  latest = s;
};

const loadEntries = (): BaselineEntry[] => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveEntries = (entries: BaselineEntry[]): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-MAX_ENTRIES)));
  } catch {
    // localStorage unavailable (private mode) — captures still work for this
    // session via the returned value / dumpBaseline, they just won't persist.
  }
};

/**
 * Snapshot the current run under a label and append it to the saved baseline.
 * Returns the entry. Call once per scenario, ideally after ↺ + a steady ~30s.
 */
export const captureBaseline = (label: string): BaselineEntry | null => {
  if (!latest) {
    // eslint-disable-next-line no-console
    console.warn('captureBaseline: no HUD stats yet — is the PerfHud open?');
    return null;
  }
  const spikes = getSpikeSummary();
  const topOffenders = spikes.offenders
    .slice(0, 3)
    .map((o) => `${o.key} (${Math.round(o.totalMs)}ms, ${o.hits} hits)`);

  const entry: BaselineEntry = {
    label,
    at: new Date().toISOString(),
    fps: latest.fps,
    fpsMin: latest.fpsMin,
    worstFrameMaxMs: Math.round(latest.worstFrameMaxMs),
    spikes: spikes.total,
    spikeWorstMs: Math.round(spikes.worstMs),
    topOffenders,
    heapUsedMb: Math.round(latest.heapUsedMb),
    heapPeakMb: Math.round(latest.heapPeakMb),
    leakMbPerMin: latest.heapLeakReady ? Math.round(latest.heapLeakMbPerMin * 10) / 10 : null,
    tweensMax: latest.activeTweensMax,
    rttMs: latest.wsRttMs,
    rttMaxMs: latest.wsRttMaxMs
  };

  const entries = loadEntries();
  entries.push(entry);
  saveEntries(entries);

  /* eslint-disable no-console */
  console.log(`Baseline captured: "${label}" (${entries.length} total)`);
  console.log(entry);
  /* eslint-enable no-console */
  return entry;
};

/** console.table of every capture so far — the at-a-glance baseline grid. */
export const dumpBaseline = (): BaselineEntry[] => {
  const entries = loadEntries();
  if (entries.length === 0) {
    // eslint-disable-next-line no-console
    console.log('No baseline captures yet. Run captureBaseline("<scenario>") first.');
    return entries;
  }
  /* eslint-disable no-console */
  console.table(
    entries.map((e) => ({
      scenario: e.label,
      'worst frame (ms)': e.worstFrameMaxMs,
      spikes: e.spikes,
      'spike worst (ms)': e.spikeWorstMs,
      'fps low': e.fpsMin,
      'mem peak (MB)': e.heapPeakMb,
      'leak (MB/min)': e.leakMbPerMin ?? '—',
      'tweens peak': e.tweensMax,
      'rtt peak (ms)': e.rttMaxMs,
      'top offender': e.topOffenders[0] ?? '—'
    }))
  );
  console.log(`${entries.length} captures. copy(getBaseline()) for the full JSON.`);
  /* eslint-enable no-console */
  return entries;
};

/** Return all captures (for `copy(getBaseline())` → paste into the baseline doc). */
export const getBaseline = (): BaselineEntry[] => loadEntries();

/** Wipe all saved captures. */
export const clearBaseline = (): void => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  // eslint-disable-next-line no-console
  console.log('Baseline cleared.');
};
